import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LangUpdateService } from '../_services/lang-update.service';
import { FieldType, PropertyPreviewMetadata, ReportPreviewMetadata, UpdateMetadata } from '../_services/report-template-design.service';
import { ElementType } from './element-type';
import { ReportCommonSection } from './report-common-section';
import { ReportElement } from './report-element';
import { ReportFonts } from './report-fonts';
import { ReportPageMargin } from './report-page-margin';
import { ReportPaperSize } from './report-paper-size';
import { ColumnList, ElementLocationInSection, PriorityList, RowList, TemplateTable } from './report-template';
import { ReportUnits } from './report-units';

export class ReportTable extends ReportElement {
  public top: string;
  public left: string;
  public width: string;
  public height: string;
  public fontSize: string;
  public cellBorder: string;
  public cellPadding: number;
  public columnList: ColumnList[];
  public rowList: RowList[];
  public priorityList: PriorityList[];
  public tableType: string;
  public tableTitle: string;
  public isCustomElementDraw?: boolean;

  private units: ReportUnits;

  private baseDef: TemplateTable;
  private paperSize: ReportPaperSize;
  private pageMargin: ReportPageMargin;
  private header: ReportCommonSection;
  private footer: ReportCommonSection;
  private readonly metaId = 'id';
  private readonly metaTop = 'y';
  private readonly metaLeft = 'x';
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaFontSize = 'fontSize';
  private readonly metaCellBorder = 'cellBorder';
  private readonly metaType = 'type';
  private readonly metaDynamicDataField = 'dynamicDataField';
  private readonly metaColumnList = 'columnList';
  private readonly metaRowList = 'rowList';
  private readonly metaTableType = 'tableType';
  private readonly metaTableTitle = 'tableTitle';
  private readonly metaCellPadding = 'cellPadding';
  private readonly metaPriorityList = 'priorityList';

  public constructor(
    index: number,
    units: ReportUnits,
    fonts: ReportFonts,
    header: ReportCommonSection,
    footer: ReportCommonSection,
    paperSize: ReportPaperSize,
    pageMargin: ReportPageMargin,
    protected translate: TranslateService,
    protected langService: LangUpdateService,
    templateTable?: TemplateTable ) {
    super(ElementType.Table, index, !(templateTable == null), translate, langService, templateTable ? templateTable.id : null);

    this.units = units;
    this.paperSize = paperSize;
    this.pageMargin = pageMargin;
    this.header = header;
    this.footer = footer;

    if (templateTable) {
      this.baseDef = templateTable;
    } else {
      this.baseDef = {
        id: this.generateUUID(),
        x: 0,
        y: 0,
        width: 100,
        height: 100,
        tableType: 'surveyTable',
        tableTitle: '',
        cellBorder: '0.5mm solid #888',
        cellPadding: 0.5,
        fontSize: 4,
        type: 'static',
        columnList: [
          { label: this.translate.instant('objects.column1'), textOrientation: 'vertical', inputType: 'checkbox', width: 10 },
          { label: this.translate.instant('objects.column2'), textOrientation: 'vertical', inputType: 'checkbox', width: 10 },
          { label: this.translate.instant('objects.column3'), textOrientation: 'vertical', inputType: 'checkbox', width: 10 },
          { label: this.translate.instant('objects.column4'), textOrientation: 'vertical', inputType: 'text', width: 20 },
          { label: this.translate.instant('objects.column5'), textOrientation: 'vertical', inputType: 'text', width: 20 },
          { label: this.translate.instant('objects.column6'), textOrientation: 'horizontal', inputType: 'text', width: 40 },
        ],
        rowList: [
          { label: this.translate.instant('objects.row1'), isCategory: false },
          { label: this.translate.instant('objects.row2'), isCategory: false },
          { label: this.translate.instant('objects.row3'), isCategory: false },
          { label: this.translate.instant('objects.row4'), isCategory: false },
        ],
        priorityList: [
          { code: this.translate.instant('objects.P1'), label: this.translate.instant('objects.emergency') },
          { code: this.translate.instant('objects.P2'), label: this.translate.instant('objects.high') },
          { code: this.translate.instant('objects.P3'), label: this.translate.instant('objects.standard') },
          { code: this.translate.instant('objects.P4'), label: this.translate.instant('objects.routine') },
          { code: this.translate.instant('objects.P5'), label: this.translate.instant('objects.observationonly') },
        ],
      } as TemplateTable;
    }

    this.top = this.units.concatenate('sizeUnit', this.baseDef[this.metaTop]);
    this.left = this.units.concatenate('sizeUnit', this.baseDef[this.metaLeft]);

    this.width = this.units.concatenate('sizeUnit', this.baseDef[this.metaWidth]);
    this.height = this.units.concatenate('sizeUnit', this.baseDef[this.metaHeight]);

    this.fontSize = this.units.concatenate('sizeUnit', this.baseDef[this.metaFontSize]);
    this.columnList = this.baseDef[this.metaColumnList];
    this.rowList = this.baseDef[this.metaRowList];
    this.priorityList = this.baseDef[this.metaPriorityList];
    this.cellBorder = this.baseDef[this.metaCellBorder];
    this.cellPadding = this.baseDef[this.metaCellPadding];
    this.tableType = this.baseDef[this.metaTableType];

    this.setChainUpdate();
    this.setPreviewMetadata();
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop],
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft],
    });
    const width = this.units.fromPx('sizeUnit', metadata.widthPx) < 150 ? 150 : this.units.fromPx('sizeUnit', metadata.widthPx);
    const height = this.units.fromPx('sizeUnit', metadata.heightPx) < 70 ? 70 : this.units.fromPx('sizeUnit', metadata.heightPx);
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: width,
      oldValue: this.baseDef[this.metaWidth],
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: height,
      oldValue: this.baseDef[this.metaHeight],
    });
  }

  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop],
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft],
    });
  }

  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop],
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft],
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaWidth,
      value: this.units.fromPx('sizeUnit', metadata.widthPx),
      oldValue: this.baseDef[this.metaWidth],
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaHeight,
      value: this.units.fromPx('sizeUnit', metadata.heightPx),
      oldValue: this.baseDef[this.metaHeight],
    });
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaLeft]);
  }

  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTop]);
  }

  public getWidthPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaWidth]);
  }

  public getHeightPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaHeight]);
  }

  public getTemplateJSON(): TemplateTable {
    return this.baseDef;
  }

  public setSectionInfo(id: string, stageName: string, location: ElementLocationInSection): void {
    this.baseDef.sectionId = id;
    this.baseDef.sectionStageTag = stageName;
    this.baseDef.elementLocationInSection = location;
  }

  public isCustomElement() {
    return this.baseDef['isCustomElementDraw'];
   }

  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaLeft,
        parentName: null,
        value: this.baseDef.x,
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        category: 'GENERAL',
      },
      {
        name: this.metaTop,
        parentName: null,
        value: this.baseDef.y,
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        category: 'GENERAL',
      },
      {
        name: this.metaWidth,
        parentName: null,
        value: this.baseDef.width,
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        category: 'GENERAL',
      },
      {
        name: this.metaHeight,
        parentName: null,
        value: this.baseDef.height,
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        category: 'GENERAL',
      },
      {
        name: this.metaTableType,
        parentName: null,
        value: this.baseDef.tableType,
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'Generic Table', value: 'genericTable' },
          { label: 'Survey Table', value: 'surveyTable' },
        ],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        category: 'TABLE',
      },
      {
        name: this.metaTableTitle,
        parentName: null,
        value: this.baseDef.tableTitle,
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        category: 'TABLE'
      },
      {
        name: this.metaCellBorder,
        parentName: null,
        value: this.baseDef.cellBorder,
        isFieldType: (type: FieldType): boolean => type === 'Text',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        category: 'TABLE',
      },
      {
        name: this.metaCellPadding,
        parentName: null,
        value: this.baseDef.cellPadding,
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        category: 'TABLE',
      },
      {
        name: this.metaFontSize,
        parentName: null,
        value: this.baseDef.fontSize,
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        category: 'TABLE',
      },
      {
        name: this.metaType,
        parentName: null,
        value: this.baseDef.type,
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [
          { label: 'static', value: 'static' },
          { label: 'dynamic', value: 'dynamic' },
          { label: 'dynamic-editable', value: 'dynamic-editable' },
        ],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        category: 'COLUMN',
      },
      {
        name: this.metaDynamicDataField, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: [{ label: 'Visual Events', value: 'visualEvents' },
        { label: 'notes', value: 'notes' },
        { label: 'Signature', value: 'signature' }], // TODO: Remove hard coded value once data types are stored in BE
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        category: 'COLUMN',
      },
      {
        name: this.metaColumnList,
        parentName: null,
        value: this.baseDef.columnList,
        isFieldType: (type: FieldType): boolean => type === 'TableHeader',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        category: 'COLUMN',
      },
      {
        name: this.metaRowList,
        parentName: null,
        value: this.baseDef.rowList,
        isFieldType: (type: FieldType): boolean => type === 'TableHeader',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        category: 'ROW',
      },
      {
        name: this.metaPriorityList,
        parentName: null,
        value: this.baseDef.priorityList,
        isFieldType: (type: FieldType): boolean => type === 'PriorityItems',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        category: 'COLUMN',
      },
    ];
  }

  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTop:
        this.baseDef[metadata.name] = metadata.value as number;
        this.top = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[metadata.name] = metadata.value as number;
        this.left = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.width = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.height = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaFontSize:
        this.baseDef[metadata.name] = metadata.value as number;
        this.fontSize = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaCellBorder:
        this.baseDef[metadata.name] = metadata.value as string;
        this.cellBorder = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaCellPadding:
        this.baseDef[metadata.name] = metadata.value as number;
        this.cellPadding = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaType:
        if (this.tableType === 'genericTable' && metadata.value !== 'static') {
          this.resizeGenericTable();
        }
      case this.metaDynamicDataField:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaColumnList:
        this.baseDef[metadata.name] = metadata.value as ColumnList[];
        this.columnList = metadata.value as ColumnList[];
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaRowList:
        this.baseDef[metadata.name] = metadata.value as RowList[];
        this.rowList = metadata.value as RowList[];
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTableType:
        this.baseDef[metadata.name] = metadata.value as string;
        this.tableType = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        if (metadata.value === 'genericTable' && this.baseDef.type !== 'static' ) {
          this.resizeGenericTable();
        }
        break;
      case this.metaTableTitle:
        this.baseDef[metadata.name] = metadata.value as string;
        this.tableTitle = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaPriorityList:
        this.baseDef[metadata.name] = metadata.value as PriorityList[];
        this.priorityList = metadata.value as PriorityList[];
        this.setPreviewValue(metadata.name, metadata.value);
        break;
    }
  }

  protected setChainUpdateFunction(subscribers: Array<Subscription>) {}

  private resizeGenericTable(): void {
    const heightNum = this.paperSize.getHeightNum() - this.pageMargin.getTopNum() -
      this.pageMargin.getBottomNum() - this.header.getHeightNum() - this.footer.getHeightNum();
    const widthNum = this.paperSize.getWidthNum() - this.pageMargin.getRightNum() - this.pageMargin.getLeftNum();
    this.top = this.units.concatenate('sizeUnit', 0 as number);
    this.left = this.units.concatenate('sizeUnit', 0 as number);
    this.height = this.units.concatenate('sizeUnit', heightNum as number);
    this.width = this.units.concatenate('sizeUnit', widthNum as number);
    this.setPreviewValue(this.metaLeft, 0);
    this.setPreviewValue(this.metaTop, 0);
    this.setPreviewValue(this.metaWidth, widthNum);
    this.setPreviewValue(this.metaHeight, heightNum);
  }
}
